import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as SuccessIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  Schedule as ScheduledIcon,
  DeleteOutline as DeletedIcon,
  Restore as RestoredIcon,
  Info as NeutralIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';
import { ActivityPeople } from '../components/ActivityPeople';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Entry {
  id: string;
  event: string;
  severity: 'info' | 'warning' | 'error';
  summary: string;
  detail: {
    results?: { platform: string; status: string; error: string | null }[];
    scheduled_for?: string;
    platforms?: string[];
    source?: string;
    was_status?: string;
    created_via?: string;
    attempts?: number;
    /** Reconstructed from post_results after the fact, not written as it happened. */
    backfilled?: boolean;
    /** A pre-audit deletion: the timestamp is real, the actor was never recorded. */
    actor_unknown?: boolean;
  } | null;
  actor: string;
  subject_type: string | null;
  subject_id: string | null;
  at: string;
}

/**
 * The icon reflects the event, not just the severity.
 *
 * Severity alone would give a green tick to "Post deleted" and "Post scheduled" — both are
 * `info`, neither is a success. A row of identical ticks also makes the list unscannable,
 * since the icon is the first thing read.
 */
const iconFor = (event: string, severity: Entry['severity']) => {
  if (severity === 'error') return { icon: <ErrorIcon fontSize="small" />, tint: 'error.main' };
  if (severity === 'warning') return { icon: <WarnIcon fontSize="small" />, tint: 'warning.main' };
  if (event.endsWith('.scheduled')) return { icon: <ScheduledIcon fontSize="small" />, tint: 'info.main' };
  if (event.endsWith('.deleted') || event.endsWith('.purged'))
    return { icon: <DeletedIcon fontSize="small" />, tint: 'text.secondary' };
  if (event.endsWith('.restored')) return { icon: <RestoredIcon fontSize="small" />, tint: 'text.secondary' };
  if (event.endsWith('.published')) return { icon: <SuccessIcon fontSize="small" />, tint: 'success.main' };
  return { icon: <NeutralIcon fontSize="small" />, tint: 'text.secondary' };
};

const when = (stamp: string) => {
  // SQLite CURRENT_TIMESTAMP is UTC with no zone marker
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? stamp
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

/** Group entries by calendar day, so a timeline reads as days rather than a flat list. */
const byDay = (entries: Entry[]) => {
  const groups = new Map<string, Entry[]>();
  entries.forEach(e => {
    const iso = e.at.includes('T') ? e.at : e.at.replace(' ', 'T') + 'Z';
    const key = new Date(iso).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    groups.set(key, [...(groups.get(key) || []), e]);
  });
  return Array.from(groups.entries());
};

/**
 * Open the exact post the entry is about.
 *
 * This used to link to a status filter instead, because Post History could only filter and not
 * deep-link. That read as working for a published post — the filtered list was short enough
 * that the post was obvious — and plainly broken for a deleted one, which is absent from the
 * list entirely and so landed on an unfiltered page. Post History now accepts `?post=<id>`
 * and fetches it directly, deleted or not.
 */
const historyLink = (e: Entry) =>
  e.subject_type === 'post' && e.subject_id
    ? `/history?post=${encodeURIComponent(e.subject_id)}`
    : '/history';

/**
 * What the app has been doing, newest first.
 *
 * Exists for the case where a scheduled post failed at 8am and nobody was watching: the
 * failure itself lands in post_results, but the attempt, the reason and the retry do not.
 * Post History shows the current state of a post; this shows the sequence of events.
 *
 * Admin-only, since entries reference other people's posts.
 */
export const ActivityLog: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'problems'>('all');
  // The timeline says what happened; People says who is using the app and what they added
  const [view, setView] = useState<'timeline' | 'people'>('timeline');
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/activity?problems=${filter === 'problems'}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      setEntries(await r.json());
    } catch {
      setError('Could not load the activity log');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">Activity Log</Typography>
          <Typography variant="body2" color="text.secondary">
            What the app has done — publishes, schedules, failures and deletions — and who is using it.
          </Typography>
        </Box>
        {view === 'timeline' && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            size="small"
            onChange={(_, v) => v && setFilter(v)}
          >
            <ToggleButton value="all">Everything</ToggleButton>
            <ToggleButton value="problems">Problems only</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Box>
        )}
      </Box>

      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="timeline" label="Timeline" />
        <Tab value="people" label="People & Keys" />
      </Tabs>

      {view === 'people' ? <ActivityPeople /> : (<>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !entries?.length ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {filter === 'problems'
                ? 'Nothing has gone wrong. Switch to Everything to see the full timeline.'
                : 'Nothing recorded yet. Publishing, scheduling and deleting a post will appear here.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        byDay(entries).map(([day, dayEntries]) => (
          <Box key={day} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}
            >
              {day}
            </Typography>

            <Card>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {dayEntries.map((e, i) => {
                  const meta = iconFor(e.event, e.severity);
                  const open = expanded === e.id;
                  // Expandable only when the panel below would actually render something.
                  // `post.scheduled` carries a detail object, but its day, time and platforms
                  // are already in the summary — so an arrow there would open onto nothing.
                  const hasDetail = Boolean(
                    e.detail?.results?.length || e.detail?.was_status
                  );

                  return (
                    <Box
                      key={e.id}
                      sx={{
                        borderTop: i ? 1 : 0,
                        borderColor: 'divider',
                        // A red edge makes a failure findable while scrolling
                        borderLeft: 3,
                        borderLeftColor:
                          e.severity === 'error'
                            ? 'error.main'
                            : e.severity === 'warning'
                              ? 'warning.main'
                              : 'transparent',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          cursor: hasDetail ? 'pointer' : 'default',
                        }}
                        onClick={() => hasDetail && setExpanded(open ? null : e.id)}
                      >
                        <Box sx={{ color: meta.tint, display: 'flex' }}>{meta.icon}</Box>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2">{e.summary}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {when(e.at)} ·{' '}
                            {e.detail?.actor_unknown ? 'actor not recorded' : e.actor}
                            {e.detail?.created_via === 'api' && ' · via API'}
                            {e.detail?.attempts !== undefined && ` · ${e.detail.attempts} attempts`}
                            {e.detail?.backfilled && ' · reconstructed'}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={e.event}
                          variant="outlined"
                          sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}
                        />

                        {e.subject_type === 'post' && (
                          <Button
                            size="small"
                            onClick={ev => {
                              ev.stopPropagation();
                              navigate(historyLink(e));
                            }}
                          >
                            View
                          </Button>
                        )}

                        {hasDetail && (
                          <IconButton size="small" sx={{ transform: open ? 'rotate(180deg)' : 'none' }}>
                            <ExpandIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <Collapse in={open}>
                        <Box sx={{ px: 2, pb: 2, pl: 6 }}>
                          {/* Per-platform outcomes are the actionable part of a failure */}
                          {e.detail?.results?.map(r => (
                            <Typography key={r.platform} variant="caption" sx={{ display: 'block' }}>
                              <strong>{getPlatformName(r.platform)}</strong>{' '}
                              <Box
                                component="span"
                                sx={{
                                  color:
                                    r.status === 'success'
                                      ? 'success.main'
                                      : r.status === 'rejected'
                                        ? 'warning.main'
                                        : 'error.main',
                                }}
                              >
                                {r.status === 'rejected' ? 'not sent' : r.status}
                              </Box>
                              {r.error && ` — ${r.error.replace(/^[a-z_]+:\s*/, '')}`}
                            </Typography>
                          ))}

                          {e.detail?.was_status && (
                            <Typography variant="caption" color="text.secondary">
                              Status at the time: {e.detail.was_status}
                              {e.detail.source && ` · from ${e.detail.source}`}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Box>
        ))
      )}
      </>)}
    </Box>
  );
};
